import React, { useEffect, useState } from 'react'
import MyAccount from '../../Components/MyAccount'
import { Container } from '@mui/material'

import PropTypes from 'prop-types'
import { alpha } from '@mui/material/styles'
import Box from '@mui/material/Box'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableSortLabel from '@mui/material/TableSortLabel'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import Checkbox from '@mui/material/Checkbox'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import DeleteIcon from '@mui/icons-material/Delete'
import FilterListIcon from '@mui/icons-material/FilterList'
import { visuallyHidden } from '@mui/utils'
import { useTranslation } from 'react-i18next'
import DirectionSnackbar from '../../Components/Snackbar'
import urls from 'utils/urls'
import { makeGetRequest } from 'utils/api'
import ListOperations from './ListOperations'
import { makePostRequest } from 'utils/api'
import CreateMessage from 'containers/MyInbox/CreateMessage'
import ImportOperationsWizard from './ImportOperationsWizard'
import { useLocation, useNavigate } from 'react-router-dom'

// LAP-733 (GE022) — column descriptors carry stable ids and translation keys.
// Labels are resolved at render time via i18next so the table headers react
// immediately to a language change.
const headCells = [
  { id: 'Nom', numeric: false, disablePadding: true, labelKey: 'mesComptes.columns.name' },
  { id: 'balance', numeric: true, disablePadding: false, labelKey: 'mesComptes.columns.balance' },
  { id: 'Devise', numeric: true, disablePadding: false, labelKey: 'mesComptes.columns.currency' },
  {
    id: 'NumeroCompte',
    numeric: true,
    disablePadding: false,
    labelKey: 'mesComptes.columns.accountNumber',
  },
  { id: 'IBAN', numeric: true, disablePadding: false, labelKey: 'mesComptes.columns.iban' },
  {
    id: 'Titulaire',
    numeric: true,
    disablePadding: false,
    labelKey: 'mesComptes.columns.holder',
  },
  {
    id: 'NumeroClient',
    numeric: true,
    disablePadding: false,
    labelKey: 'mesComptes.columns.clientNumber',
  },
  {
    id: 'TypeCompte',
    numeric: true,
    disablePadding: false,
    labelKey: 'mesComptes.columns.accountType',
  },
]

function EnhancedTableHead(props) {
  const { onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } = props
  const { t } = useTranslation('common')
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property)
  }

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{
              'aria-label': 'select all desserts',
            }}
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
              sx={{ fontWeight: 600 }}
            >
              {t(headCell.labelKey)}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  )
}

EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
}

const EnhancedTableToolbar = (props) => {
  const { numSelected, deleteBankAccount, accounts } = props
  const { t } = useTranslation('common')

  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        ...(numSelected > 0 && {
          bgcolor: (theme) =>
            alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
        }),
      }}
    >
      {numSelected > 0 ? (
        <Typography sx={{ flex: '1 1 100%' }} color="inherit" variant="subtitle1" component="div">
          {t('mesComptes.selected', { count: numSelected })}
        </Typography>
      ) : (
        <Typography sx={{ flex: '1 1 100%' }} variant="h6" id="tableTitle" component="div">
          {t('mesComptes.titleWithCount', { count: accounts.length })}
        </Typography>
      )}

      {numSelected > 0 ? (
        <Tooltip title={t('mesComptes.actions.delete')}>
          <IconButton onClick={deleteBankAccount}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title={t('mesComptes.actions.filter')}>
          <IconButton>
            <FilterListIcon />
          </IconButton>
        </Tooltip>
      )}
    </Toolbar>
  )
}

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
}
function MesComptes() {
  // LAP-733 (GE022) — page-level i18n
  const { t } = useTranslation('common')
  const location = useLocation()
  const navigate = useNavigate()
  const [mOpen, setMOpen] = useState(false)
  const [selectedRows, setSelectedRows] = useState([])
  const [operations, setOperations] = useState({})

  // const [accounts, setAccounts] = useState([])
  const [mainAccounts, setMainAccounts] = useState([])
  const [utable, setUtable] = useState(false)
  const [search, setSearch] = useState('')
  // const [loading, setLoading] = useState(false)
  const [openSnack, setOpenSnak] = useState({
    message: '',
    color: '',
    open: false,
  })
  const [importWizardOpen, setImportWizardOpen] = useState(false)
  const [openAddAccountFromUrl, setOpenAddAccountFromUrl] = useState(false)

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const action = searchParams.get('action')
    const modal = searchParams.get('modal')
    const urlAction = action || modal

    if (urlAction === 'new-account') {
      setOpenAddAccountFromUrl(true)
    }

    if (urlAction === 'import-account') {
      setImportWizardOpen(true)
      navigate(location.pathname, { replace: true })
    }
  }, [location.pathname, location.search, navigate])

  useEffect(() => {
    // LAP — Perfs : on supprime le fetch redondant /api/Finance/AllAccounts/.
    // Les données affichées proviennent uniquement de `operations` (servi
    // par /api/Finance/account-operations/ avec balance + relations préfetchées).
    // `mainAccounts` n'est plus utilisé pour le rendu mais reste disponible
    // pour searchTable() côté client.
    fetchOperations()
  }, [utable])
  const fetchOperations = async (pagination = '') => {
    // setLoading(true)
    let url = urls.accountOperations
    if (pagination !== '') {
      url += pagination
    }
    const response = await makeGetRequest(url, {}, true)
    if (response.status === 200) {
      setOperations({ ...response.data })
      // setLoading(false)
    }

    // setLoading(false)
  }

  const deleteBankAccount = async (e) => {
    if (selectedRows.length === 0) {
      alert(t('mesComptes.messages.selectAccount'))
    } else {
      await makePostRequest(
        urls.delAccounts,
        {
          account_ids: selectedRows,
        },
        {},
        true,
      )
      // LAP — Perfs : on évite l'appel /api/Finance/Accounts/ qui re-déclenche
      // la sérialisation lourde de tous les comptes. updateTable() ci-dessous
      // refait déjà le fetch via fetchOperations() (qui passe par le viewset
      // optimisé avec prefetch_related + annotate).
      setOpenSnak({
        open: true,
        color: 'success',
        message: t('mesComptes.messages.deleteSuccess'),
      })
      updateTable()
      setSelectedRows([])
      setTimeout(() => {
        setOpenSnak({
          open: false,
          color: 'success',
          message: t('mesComptes.messages.addSuccess'),
        })
      }, 2000)
    }
  }

  const searchTable = () => {
    let searchVal = search.toLowerCase()
    let tmpAccounts = []
    for (let a of mainAccounts) {
      let keys = Object.keys(a)

      for (let k of keys) {
        let cValue = (a[k] + '').toLowerCase()

        if (cValue.indexOf(searchVal) !== -1) {
          tmpAccounts.push(a)
          break
        }
      }
    }
    // setAccounts(tmpAccounts)
  }

  const updateSearch = (e) => {
    setSearch(e.target.value)
  }

  const updateTable = (e) => {
    setUtable(!utable)
  }
  const handelMOpen = () => {
    if (selectedRows.length === 1) {
      setMOpen(true)
    } else {
      alert(t('mesComptes.messages.selectOneToShare'))
    }
  }

  const handleImportComplete = () => {
    setOpenSnak({
      open: true,
      color: 'success',
      message: t('mesComptes.messages.importSuccess'),
    })
    updateTable()
    fetchOperations()
    setTimeout(() => {
      setOpenSnak({
        open: false,
        color: '',
        message: '',
      })
    }, 3000)
  }

  return (
    <>
      {mOpen && (
        <CreateMessage
          setMOpen={setMOpen}
          title={t('mesComptes.share.dialogTitle')}
          settingId={5}
          mescompte={operations.results.filter((v) => v.id === selectedRows[0])[0]}
        />
      )}
      <ImportOperationsWizard
        open={importWizardOpen}
        onClose={() => setImportWizardOpen(false)}
        accounts={operations.results || []}
        onImportComplete={handleImportComplete}
      />
      <MyAccount
        updateSearch={updateSearch}
        deleteBankAccount={deleteBankAccount}
        searchTable={searchTable}
        updateTable={updateTable}
        setSelectedRows={setSelectedRows}
        selectedRows={selectedRows}
        handelMOpen={handelMOpen}
        onImportClick={() => setImportWizardOpen(true)}
        openAddAccountFromUrl={openAddAccountFromUrl}
        onAddAccountUrlConsumed={() => {
          setOpenAddAccountFromUrl(false)
          navigate(location.pathname, { replace: true })
        }}
      />
      <Container maxWidth="xl" className="pt-4">
        {openSnack.open && (
          <DirectionSnackbar message={openSnack.message} color={openSnack.color} />
        )}

        <Box sx={{ width: '100%' }}>
          <ListOperations
            fetchOperations={fetchOperations}
            setOperations={setOperations}
            setSelectedRows={setSelectedRows}
            operations={operations}
          />
        </Box>
      </Container>
    </>
  )
}

export default MesComptes
